import { SERIAL_STRING, STATUS, WRITE } from './traceabilityLink';
import {
  PROVIDER,
  RELEASE_REASON,
  confirmSerialString,
  releaseSerialString,
  reserveSerialString,
} from './serialNumberProvider';
import { logProtocol, logProtocolError } from './traceabilityLog';

/**
 * Cómo se averigua con qué número se identifica una placa recién vinculada.
 *
 * Es el orden que reemplaza al permiso de WebUSB del principio: primero se mira
 * la EEPROM, que es donde tiene que estar el número, y recién si ahí no hay nada
 * se cae a los caminos de excepción -- el serial del ch340 de las pocas placas
 * que se entregaron sin grabar, y el servidor de seriales para las que no tienen
 * ni eso.
 *
 * No sabe nada de React: avisa por listeners y quien quiera lo pinta. Lo que sí
 * necesita de afuera son las dos cosas que no puede hacer sola -- grabar el
 * firmware, que es del panel, y leer el serial por WebUSB, que necesita un click
 * del usuario -- y las recibe como funciones.
 *
 * Una por puerto. Quien la crea y la tira es el que sigue a los puertos.
 */

export const IDENTITY = {
  IDLE: 'IDLE',
  // Preguntándole a la placa si tiene el firmware.
  PROBING: 'PROBING',
  // No lo tiene, o el que tiene es anterior al número de producción. La UI pide
  // instalarlo, y acá no se sigue hasta que se instale: sin firmware no hay
  // manera de saber quién es esta placa.
  FIRMWARE_MISSING: 'FIRMWARE_MISSING',
  INSTALLING: 'INSTALLING',
  // Leyendo el número de la EEPROM.
  READING: 'READING',
  // La EEPROM no tiene número. Hace falta un click para pedir el permiso de
  // WebUSB, así que acá se espera al usuario.
  NEEDS_USB: 'NEEDS_USB',
  READING_USB: 'READING_USB',
  // Pidiendo un número al servidor, grabándolo y confirmándolo.
  PROVISIONING: 'PROVISIONING',
  // Hay respuesta: con número o sin él. Ver `identity` en el snapshot.
  READY: 'READY',
  // La placa se fue, o el que la seguía se desmontó, antes de terminar.
  ABORTED: 'ABORTED',
};

/**
 * De dónde salió el número, que viaja con el evento. 'assigned' es el de la
 * sesión en la que se le asignó uno: la próxima vez esa misma placa va a leerse
 * como 'eeprom', y poder distinguir el momento en que se numeró es justamente lo
 * que permite auditar contra lo que entregó el servidor.
 */
export const SOURCE = {
  EEPROM: 'eeprom',
  WEBUSB: 'webusb',
  ASSIGNED: 'assigned',
};

/**
 * Cuántas veces se reintenta la lectura antes de tratar a la placa como una sin
 * número. Un error de lectura no es una EEPROM vacía -- puede ser ruido en la
 * UART o una placa ocupada --, y darla por vacía la mandaría al camino de
 * asignarle un número que quizá ya tiene.
 */
const READ_ATTEMPTS = 2;

/**
 * Cuántas veces se vuelve a buscar el firmware, en silencio, en una placa a la
 * que se le acaba de cargar código con él antes de pedir que se instale.
 *
 * Ahí el silencio casi seguro es el loop del usuario ocupado, y el modal de
 * instalación graba el sketch vacío encima de su programa. Pero no se insiste
 * para siempre: si la compilación falló, la placa sigue con lo que tenía antes, y
 * eso sí puede no tener firmware.
 */
const EXPECTED_FIRMWARE_ROUNDS = 3;

// Qué se le dice al servidor cuando el número que entregó no se pudo grabar.
const releaseReasonFor = (writeStatus) => {
  if (writeStatus === WRITE.INVALID) return RELEASE_REASON.INVALID_FORMAT;
  if (writeStatus === WRITE.UNVERIFIED) return RELEASE_REASON.VERIFY_FAILED;
  return RELEASE_REASON.WRITE_FAILED;
};

class BoardIdentity {
  constructor(link, {
    portName = '',
    context = {},
    installFirmware = null,
    /**
     * Si hay motivos para creer que la placa tiene el firmware aunque no conteste:
     * esta pestaña le acaba de cargar código con el firmware embebido. Ver
     * EXPECTED_FIRMWARE_ROUNDS.
     */
    expectsFirmware = () => false,
    readUsbSerial = null,
    readGrantedUsbSerial = null,
    provider = null,
  } = {}) {
    this.link = link;
    this.portName = portName;
    // Lo que se sabe de la placa sin preguntarle: qué modelo es y quién la está
    // usando. Va tal cual al servidor de seriales, que es quien elige el número.
    this.context = context;
    this.installFirmware = installFirmware;
    this.expectsFirmware = expectsFirmware;
    /**
     * El camino de excepción: las pocas placas entregadas con el serial en el
     * ch340 y nada en la EEPROM. Necesita un click porque WebUSB solo abre su
     * selector dentro de un gesto del usuario. Sin esta función -- un transporte
     * que no tiene WebUSB -- ese paso simplemente no existe.
     */
    this.readUsbSerial = readUsbSerial;
    /**
     * Lo mismo pero sin abrir el selector: el permiso de WebUSB queda dado para
     * siempre una vez que el usuario lo dio, y leerlo de los permisos ya
     * otorgados no necesita gesto ninguno.
     *
     * Con esto, una placa del ch340 cuyo serial no entra en la EEPROM pide el
     * permiso una sola vez por navegador en lugar de un click por sesión. Son
     * pocas y van a ir desapareciendo, pero mientras existan no tienen por qué
     * molestar cada vez.
     */
    this.readGrantedUsbSerial = readGrantedUsbSerial;
    this.provider = provider || { reserve: reserveSerialString, confirm: confirmSerialString, release: releaseSerialString };

    this.state = IDENTITY.IDLE;
    this.identity = { serialString: null, hardwareSerial: null, source: null };
    /**
     * El número que el servidor reservó y todavía no se confirmó. Se guarda para
     * poder devolverlo si la placa se va en el medio: un número reservado que no
     * se devuelve queda sin dueño hasta que venza la reserva.
     */
    this.reserved = null;
    this.listeners = new Set();
    this.disposed = false;
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot = () => ({
    state: this.state,
    identity: { ...this.identity },
  })

  emit = () => {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  setState = (state) => {
    if (this.state === state || this.disposed) return;
    this.state = state;
    this.emit();
  }

  /**
   * Se terminó de averiguar, con número o sin él.
   *
   * Sin número también es una respuesta y no un error: una placa a la que no se
   * le pudo asignar uno se reporta igual, con el campo vacío, en vez de quedar
   * sin reportarse. Perder la conexión de una placa es peor que reportarla sin
   * identificar.
   */
  resolve = (serialString, source) => {
    this.identity = {
      serialString: serialString || null,
      hardwareSerial: this.link ? this.link.serialNumber : null,
      source: serialString ? source : null,
    };
    // Un solo aviso, cambie el estado o no: resolver dos veces deja el estado
    // igual y el resultado distinto, y ese cambio también hay que contarlo.
    if (this.state === IDENTITY.READY) this.emit();
    else this.setState(IDENTITY.READY);
    return this.identity;
  }

  /**
   * Arranca: ¿tiene firmware esta placa?
   *
   * Es lo primero porque todo lo demás cuelga de ahí: sin firmware no se puede
   * leer la EEPROM ni grabarla, y el camino de WebUSB tampoco sirve de nada si
   * después no hay dónde guardar lo que se lea.
   */
  start = async () => {
    if (this.disposed || this.state !== IDENTITY.IDLE) return this.snapshot();
    this.setState(IDENTITY.PROBING);

    let status = await this.link.start();
    for (let round = 1; status !== STATUS.READY && !this.disposed && round <= EXPECTED_FIRMWARE_ROUNDS && this.expectsFirmware(); round += 1) {
      logProtocol(this.portName, `No contestó, pero se le cargó código con el firmware: se la vuelve a buscar (${round}/${EXPECTED_FIRMWARE_ROUNDS}).`);
      this.link.rearm();
      status = await this.link.start(); // eslint-disable-line no-await-in-loop
    }
    if (this.disposed) return this.snapshot();
    if (status !== STATUS.READY) {
      this.setState(IDENTITY.FIRMWARE_MISSING);
      return this.snapshot();
    }
    return this.read();
  }

  /**
   * Instala el firmware y vuelve a empezar. Lo llama la UI cuando el usuario
   * aprieta el botón del modal, que es obligatorio: una placa sin firmware no se
   * puede identificar, así que no hay un "más tarde" que deje algo usable.
   *
   * Una instalación que falla vuelve al mismo modal en vez de seguir de largo.
   * Es lo único razonable: seguir sería reportar la placa sin identificar
   * habiendo un camino que todavía no se agotó.
   */
  install = async () => {
    if (this.disposed || !this.installFirmware) return this.snapshot();
    /**
     * Un segundo pedido mientras el primero sigue andando se ignora. El botón del
     * modal queda deshabilitado mientras se instala, pero el estado tarda en
     * llegar hasta él, y dos instalaciones encimadas se pelean por el puerto.
     */
    if (this.state === IDENTITY.INSTALLING) return this.snapshot();
    this.setState(IDENTITY.INSTALLING);

    try {
      // El puerto se suelta antes: el flasheo lo necesita para él solo.
      await this.link.reset();
      await this.installFirmware();
    } catch (error) {
      logProtocolError(this.portName, 'No se pudo instalar el firmware:', error && error.message);
      this.setState(IDENTITY.FIRMWARE_MISSING);
      return this.snapshot();
    }
    if (this.disposed) return this.snapshot();

    // Con la espera larga: la placa se acaba de reiniciar y, con USB nativo,
    // todavía puede estar volviendo a enumerarse.
    const status = await this.link.start({ settle: this.link.rebootSettle });
    if (this.disposed) return this.snapshot();
    if (status !== STATUS.READY) {
      this.setState(IDENTITY.FIRMWARE_MISSING);
      return this.snapshot();
    }
    return this.read();
  }

  /**
   * Lee el número de la EEPROM, que es el caso normal y el que no muestra nada
   * en pantalla: la placa ya tiene número y el evento sale con él.
   */
  read = async () => {
    if (this.disposed) return this.snapshot();
    this.setState(IDENTITY.READING);

    for (let attempt = 1; attempt <= READ_ATTEMPTS; attempt += 1) {
      const result = await this.link.readSerialString(); // eslint-disable-line no-await-in-loop
      if (this.disposed) return this.snapshot();

      if (result.status === SERIAL_STRING.PRESENT) {
        this.resolve(result.value, SOURCE.EEPROM);
        return this.snapshot();
      }
      /**
       * El firmware está pero es anterior al número de producción. Se trata como
       * si faltara, porque la salida es la misma: reinstalarlo. Que conteste el
       * ping no lo hace útil para lo que hace falta acá.
       */
      if (result.status === SERIAL_STRING.UNSUPPORTED) {
        this.setState(IDENTITY.FIRMWARE_MISSING);
        return this.snapshot();
      }
      if (result.status === SERIAL_STRING.EMPTY) break;
    }

    /**
     * Sin número en la EEPROM. Antes de pedirle nada al usuario se prueba con
     * los permisos que ya estén dados, que no abren ningún diálogo: si esta
     * placa ya pasó por el selector alguna vez, se resuelve sin que nadie se
     * entere.
     */
    if (this.readGrantedUsbSerial) {
      const granted = await this.tryUsbSerial(this.readGrantedUsbSerial);
      if (this.disposed || granted) return this.snapshot();
    }
    // No hubo suerte: queda pedir el permiso, y eso necesita un click.
    if (this.readUsbSerial) {
      this.setState(IDENTITY.NEEDS_USB);
      return this.snapshot();
    }
    return this.provision();
  }

  /**
   * El usuario apretó "leer datos". Es el camino de las pocas placas entregadas
   * con el serial grabado en el ch340.
   *
   * Lo que se lee se intenta grabar también en la EEPROM, para que esa placa
   * quede normalizada y no dependa de WebUSB nunca más. Si no se puede grabar se
   * reporta igual con ese número: lo que el backend ya conoce de esas placas es
   * justamente ese serial, así que vale como identidad aunque la EEPROM siga
   * vacía. El caso tiene nombre porque es probable: el serial del ch340 no tiene
   * por qué medir los 8 caracteres que la placa acepta.
   */
  readUsb = async () => {
    if (this.disposed || !this.readUsbSerial) return this.snapshot();
    this.setState(IDENTITY.READING_USB);

    const read = await this.tryUsbSerial(this.readUsbSerial);
    if (this.disposed || read) return this.snapshot();
    return this.provision();
  }

  /**
   * Lee el serial por WebUSB con la función que le den y, si hay algo, cierra
   * con él. Devuelve si alcanzó para identificar la placa.
   *
   * Un permiso rechazado o un selector cerrado no son un error del paso: son la
   * respuesta de que por ahí no viene, y el que llama sigue con lo que le queda.
   */
  tryUsbSerial = async (read) => {
    let usbSerial = null;
    try {
      usbSerial = await read();
    } catch (error) {
      usbSerial = null;
    }
    if (this.disposed || !usbSerial) return false;

    const written = await this.link.writeSerialString(usbSerial);
    if (this.disposed) return false;
    if (written.status === WRITE.OK) {
      logProtocol(this.portName, 'El serial del ch340 quedó grabado en la EEPROM:', usbSerial);
    } else {
      logProtocolError(this.portName, `El serial del ch340 (${usbSerial}) no se pudo grabar en la EEPROM; la placa se reporta con él igual.`);
    }
    this.resolve(usbSerial, SOURCE.WEBUSB);
    return true;
  }

  /**
   * La placa no tiene número en ningún lado: se le pide uno al servidor, se lo
   * graba y se avisa que quedó ocupado.
   *
   * Cualquier tropiezo termina con la placa reportada sin número en vez de
   * trabada. Un número reservado que no se llegó a grabar se devuelve, para que
   * no se queme: eso es lo único que no puede saltearse.
   */
  provision = async () => {
    if (this.disposed) return this.snapshot();
    this.setState(IDENTITY.PROVISIONING);

    const reservation = await this.provider.reserve({
      ...this.context,
      hardwareSerial: this.link.serialNumber,
      firmwareVersion: this.link.firmwareVersion,
    });
    if (this.disposed) {
      await this.releaseReserved(RELEASE_REASON.ABORTED, reservation.serialString);
      return this.snapshot();
    }
    if (reservation.status !== PROVIDER.OK) {
      if (reservation.status !== PROVIDER.DISABLED) {
        logProtocolError(this.portName, 'La placa queda sin número de producción:', reservation.status);
      }
      this.resolve(null, null);
      return this.snapshot();
    }

    this.reserved = reservation.serialString;
    const written = await this.link.writeSerialString(reservation.serialString);
    if (written.status !== WRITE.OK) {
      await this.releaseReserved(releaseReasonFor(written.status));
      this.resolve(null, null);
      return this.snapshot();
    }

    this.reserved = null;
    /**
     * La confirmación no condiciona el resultado: el número ya está en la placa,
     * así que el evento sale con él haya llegado o no el aviso. Un aviso perdido
     * es un problema del servidor con su inventario, no de esta placa con su
     * identidad.
     */
    await this.provider.confirm({
      serialString: reservation.serialString,
      hardwareSerial: this.link.serialNumber,
    });
    if (this.disposed) return this.snapshot();
    this.resolve(reservation.serialString, SOURCE.ASSIGNED);
    return this.snapshot();
  }

  releaseReserved = async (reason, serialString = this.reserved) => {
    if (!serialString) return;
    this.reserved = null;
    await this.provider.release({ serialString, reason });
  }

  /**
   * La placa se fue o el que la seguía se desmontó. Lo único que hay que
   * deshacer es el número reservado sin grabar.
   */
  dispose = async () => {
    if (this.disposed) return;
    this.disposed = true;
    if (this.state !== IDENTITY.READY) this.state = IDENTITY.ABORTED;
    this.listeners.clear();
    await this.releaseReserved(RELEASE_REASON.ABORTED);
  }
}

export default BoardIdentity;
